<template>
  <div
    class="ph5-ns ph3 black-70"
    v-loading.fullscreen="loading"
  >
    <div v-if="form">
      <h1>Scaffold Form</h1>
      <div class="ma4"></div>
      <div class="bordered-block">
        <el-form
          :model="form"
          :rules="rules"
          ref="form"
          label-width="140px"
        >
          <el-form-item label="Id" prop="id">
            <el-input
              :disabled="editing"
              v-model="form.id"
            ></el-input>
          </el-form-item>
          <el-form-item label="Name" prop="name">
            <el-input v-model="form.name"></el-input>
          </el-form-item>
          <el-form-item label="Header" prop="header">
            <el-input v-model="form.header"></el-input>
          </el-form-item>
          <el-form-item label="Frontend" prop="frontend">
            <el-input v-model="form.frontend"></el-input>
          </el-form-item>
          <el-form-item label="Database" prop="database">
            <el-input v-model="form.database"></el-input>
          </el-form-item>
          <el-form-item label="Version" prop="version">
            <el-input v-model="form.version"></el-input>
          </el-form-item>
          <el-form-item label="Upload">
            <label
              v-if="!filesLoaded"
              class="el-button el-button--default"
            >
              Folder
              <input
                type="file"
                style="display:none"
                @change="fileChanged($event.target.files)"
                webkitdirectory
                directory
              />
            </label>
            <div
              v-if="filesError"
              class="el-form-item__error"
            >
              {{ filesError }}
            </div>

            <el-button
              :plain="true"
              type="danger"
              v-if="filesLoaded"
              @click.prevent="clearFiles(null)"
              >Clear</el-button
            >
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="save()"
              >Save</el-button
            >

            <el-popover
              ref="deleteConfirm"
              placement="top"
              width="160"
              v-model="deleteConfirmOpened"
            >
              <p>Are you sure to delete this?</p>
              <div style="text-align: right; margin: 0">
                <el-button
                  size="mini"
                  type="text"
                  @click="deleteConfirmOpened = false"
                  >Cancel</el-button
                >
                <el-button
                  type="danger"
                  size="mini"
                  @click="deleteScaffold()"
                  >Confirm</el-button
                >
              </div>
            </el-popover>

            <el-button
              v-if="editing"
              v-popover:deleteConfirm
              >Delete</el-button
            >

            <router-link class="el-button" to="/"
              >Cancel</router-link
            >
          </el-form-item>
        </el-form>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import scaffoldStorage from '../scaffold-storage';
import scaffoldService from '../scaffold-service';
import scaffoldFolderExtractor from './scaffold-folder-extractor';

export default {
  name: 'app-scaffold-form',

  props: ['scaffoldId'],

  data() {
    return {
      scaffold: null,
      files: [],
      editing: false,
      rules: null,
      form: null,
      filesLoaded: false,
      filesError: null,
      loading: true,
      deleteConfirmOpened: false,
    };
  },

  computed: {
    creating() {
      return !this.editing;
    },
  },

  async created() {
    try {
      const authenticated = await this.$store.getters[
        'auth/authenticatedAsync'
      ]();

      if (!authenticated) {
        this.$router.push('/');
        return;
      }

      this.loading = true;
      this.editing = !!this.scaffoldId;

      if (this.editing) {
        this.scaffold = await scaffoldService.find(
          this.scaffoldId,
        );
        this.buildRules();
        this.buildForm();
        this.loading = false;
      } else {
        this.scaffold = {};
        this.buildRules();
        this.buildForm();
        this.loading = false;
      }
    } catch (error) {
      this.handleError(error);
    }
  },

  methods: {
    isValid() {
      return new Promise((resolve) => {
        this.$refs.form.validate((valid) => {
          resolve(valid && this.filesLoaded);
        });
      });
    },

    buildRules() {
      const context = this;

      this.rules = {
        name: [
          {
            required: true,
            message: 'Name is required',
            trigger: 'blur',
          },
        ],
        header: [
          {
            required: true,
            message: 'Header is required',
            trigger: 'blur',
          },
        ],
        frontend: [
          {
            required: true,
            message: 'Frontend is required',
            trigger: 'blur',
          },
        ],
        database: [
          {
            required: true,
            message: 'Database is required',
            trigger: 'blur',
          },
        ],
        id: [
          {
            required: true,
            message: 'Id is required',
            trigger: 'blur',
          },
          {
            min: 3,
            max: 27,
            message: 'Length should be 3 to 27',
            trigger: 'blur',
          },
        ],
        version: {
          required: true,
          message: 'Version is required',
          trigger: 'blur',
        },
      };
    },

    buildForm() {
      this.form = {
        id: this.scaffold.id,
        name: this.scaffold.name,
        header: this.scaffold.header,
        version: this.scaffold.version || '1.0.0',
        database: this.scaffold.database,
        frontend: this.scaffold.frontend,
      };
    },

    async fileChanged(files) {
      this.loading = true;

      if (!files || files.length <= 0) {
        this.clearFiles(null);
        return;
      }

      try {
        const extractedFiles = await scaffoldFolderExtractor.extract(
          files,
          0,
        );

        this.mountFiles(extractedFiles);
      } catch (error) {
        this.clearFiles(error);
      }
    },

    mountFiles(files) {
      this.filesLoaded = true;
      this.loading = false;
      this.filesError = null;
      this.files = files;
    },

    clearFiles(error) {
      this.filesLoaded = false;
      this.loading = false;
      this.filesError =
        error && error.message ? error.message : error;
      this.files = [];
    },

    async deleteScaffold() {
      this.deleteConfirmOpened = false;
      this.loading = true;

      try {
        await scaffoldService.delete(this.scaffold);
        this.loading = false;
        this.$router.push('/');
      } catch (error) {
        this.handleError(error);
      }
    },

    async save() {
      try {
        const valid = await this.isValid();

        if (!valid) {
          return;
        }

        this.loading = true;
        this.scaffold.name = this.form.name;
        this.scaffold.header = this.form.header;
        this.scaffold.database = this.form.database;
        this.scaffold.frontend = this.form.frontend;
        this.scaffold.id = this.form.id;
        this.scaffold.version = this.form.version;

        if (this.editing) {
          await this.update();
        } else {
          await this.create();
        }
      } catch (error) {
        this.handleError(error);
      }
    },

    async update() {
      try {
        await scaffoldService.validateForUpdate(
          this.scaffold.id,
          this.scaffold.version,
        );
        await this.upload();
        await scaffoldService.update(
          this.scaffold,
          this.files,
        );
        this.navigateToScaffold();
      } catch (error) {
        this.handleError(error);
      }
    },

    async create() {
      try {
        await scaffoldService.validateForCreate(
          this.scaffold.id,
          this.scaffold.version,
        );
        await this.upload();
        await scaffoldService.create(
          this.scaffold,
          this.files,
        );
        this.navigateToScaffold();
      } catch (error) {
        this.handleError(error);
      }
    },

    navigateToScaffold() {
      this.$router.push(`/scaffold/${this.scaffold.id}`);
    },

    upload() {
      return scaffoldStorage.upload(
        this.scaffold,
        this.files,
      );
    },

    handleError(error) {
      this.loading = false;
      this.$store.dispatch('core/error', { error });
      this.clearFiles();
    },
  },
};
</script>

<style></style>
